import { FC } from 'react';
import {
  amountBasicFieldFormattingProps,
  numericBasicFieldFormattingProps,
} from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { FormValue } from '@app/shared/components/form-controls/form-value/FormValue';
import { EquityItem } from '../../CapitalStructureItemFormERF2';
import { useLocale } from '@app/core/hooks/useLocale';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import { currencyDenominationPair } from '@app/shared/helpers';
import styles from '../../capital-structure-item-form-2.module.scss';
import classNames from 'classnames';
import {
  AdjustmentMode,
  OwnershipSplitType,
  OwnerType,
} from '@app/shared/models/contracts/enums/shared-enums';
import CapitalStructureValuesTableCell from './CapitalStructureValuesTableCell';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import buttonStyles from '@app/shared/components/button/button.module.scss';
import SvgTabArrowActive from '@app/shared/icons/TabArrowActive';
import { setCapitalStructureBuildStructureErf } from '@app/core/store/ui-values-slice';
import { TableSection } from './types';
import { getSharesLabel } from '@app/shared/utils/LabelHelper';

interface CapitalStructureEditTableProps {
  equityItem: EquityItem;
  section: keyof typeof OwnerType | OwnershipSplitType.ProRata;
  renderingSplitType: OwnershipSplitType;
  activeTabs: number[];
  setActiveTabs: (tabs: number[]) => void;
  currentEquityId: string;
  eventId?: string;
  isOrdinaryEquity?: boolean;
}

const PARENT_CLASSNAME = 'capital-structure-values-table';

const CapitalStructureEditTable: FC<CapitalStructureEditTableProps> = ({
  equityItem,
  section,
  renderingSplitType,
  activeTabs,
  setActiveTabs,
  currentEquityId,
  eventId,
  isOrdinaryEquity = false,
}) => {
  const { l } = useLocale();
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.project.projectDraft);
  const { currency, denomination } = project.details;

  const isProRata = section === OwnershipSplitType.ProRata;
  const ownershipIndex = Object.values(equityItem.ownership).findIndex(
    (owner) => owner.owner === section
  );
  const hasValues =
    Number(equityItem.ownership[ownershipIndex]?.amount) > 0 ||
    Number(equityItem.ownership[ownershipIndex]?.currentAmount) > 0;

  const returnNullIfZero = (value: number) => (value === 0 ? null : value);

  const isProRataSelected = isProRata && renderingSplitType === OwnershipSplitType.ProRata;
  const isManualSelected = !isProRata && renderingSplitType === OwnershipSplitType.Manual;
  const isProRataAmountAdjusted = equityItem.proRataAmountInputMode === AdjustmentMode.Relative;
  const isProRataAmountClosing = equityItem.proRataAmountInputMode === AdjustmentMode.Absolute;
  const isProRataSharesAdjusted = equityItem.proRataSharesInputMode === AdjustmentMode.Relative;
  const isProRataSharesClosing = equityItem.proRataSharesInputMode === AdjustmentMode.Absolute;
  const isManualAmountAdjusted =
    equityItem.ownership[ownershipIndex]?.amountInputMode === AdjustmentMode.Relative;
  const isManualAmountClosing =
    equityItem.ownership[ownershipIndex]?.amountInputMode === AdjustmentMode.Absolute;
  const isManualSharesAdjusted =
    equityItem.ownership[ownershipIndex]?.numberOfSharesInputMode === AdjustmentMode.Relative;
  const isManualSharesClosing =
    equityItem.ownership[ownershipIndex]?.numberOfSharesInputMode === AdjustmentMode.Absolute;
  const shouldShowRealBuybackValue =
    isOrdinaryEquity &&
    ((isProRata && equityItem.totalValueDelta < 0) ||
      (!isProRata &&
        equityItem.valueDeltas[section] != null &&
        equityItem.valueDeltas[section] < 0));

  const handleOpenedTab = (index: number) => {
    const newActiveTabs = activeTabs.includes(index)
      ? activeTabs.filter((tab) => tab !== index)
      : [...activeTabs, index];
    setActiveTabs(newActiveTabs);
    dispatch(
      setCapitalStructureBuildStructureErf({
        key: `${currentEquityId}-${eventId}`,
        value: newActiveTabs,
      })
    );
  };

  return (
    <div>
      <table
        className={classNames(styles[`${PARENT_CLASSNAME}`], {
          [styles[`${PARENT_CLASSNAME}__without-margin`]]:
            !activeTabs.includes(ownershipIndex) && !isProRata,
        })}>
        <thead>
          <tr>
            {section === OwnershipSplitType.ProRata ? (
              <th className={styles[`${PARENT_CLASSNAME}--prorata`]}>{l('_Aggregate')}</th>
            ) : (
              <>
                <th
                  colSpan={3}
                  className={classNames(styles['capital-structure-form__tab'], {
                    [styles['capital-structure-form__tab--full-width']]:
                      !activeTabs.includes(ownershipIndex),
                  })}>
                  <Button
                    data-testid="owner-tab"
                    appearance={ButtonAppearance.ACCORDION_TAB}
                    size={ButtonSize.FULL_WIDTH}
                    ignoreReadOnly
                    className={classNames({
                      [buttonStyles['button--accordion-tab-active']]:
                        activeTabs.includes(ownershipIndex),

                      [buttonStyles['button--accordion-tab-non-empty']]: hasValues,
                    })}
                    onClick={() => handleOpenedTab(ownershipIndex)}>
                    {OwnerType[section]}
                    <div className={classNames(styles['capital-structure-form__tab-action'])}>
                      {activeTabs.includes(ownershipIndex) ? (
                        <SvgTabArrowActive />
                      ) : (
                        <div className={classNames(styles['capital-structure-form__icon-active'])}>
                          <SvgTabArrowActive />
                        </div>
                      )}
                    </div>
                  </Button>
                </th>
                <th />
                <th />
              </>
            )}
          </tr>
          {section === OwnershipSplitType.ProRata && (
            <tr className={styles[`${PARENT_CLASSNAME}__column-header-row`]}>
              <th />
              <td>
                {l('_AmountCurrency', {
                  currency: currencyDenominationPair(currency, denomination),
                })}
              </td>
              <td>{getSharesLabel(denomination)}</td>
            </tr>
          )}
        </thead>
        {(isProRata || activeTabs.includes(ownershipIndex)) && (
          <tbody>
            <tr>
              <td className={styles[`${PARENT_CLASSNAME}__label-column`]}>
                {l('_OpeningBalance')}
              </td>
              <td>
                <FormValue
                  value={returnNullIfZero(
                    isProRata
                      ? equityItem.totalValueBeforeMovements
                      : equityItem.ownershipBeforeMovements[section]?.amount
                  )}
                  normalize
                  {...amountBasicFieldFormattingProps}
                  autoHeight
                />
              </td>
              <td>
                <FormValue
                  value={returnNullIfZero(
                    isProRata
                      ? equityItem.totalSharesBeforeMovements
                      : equityItem.ownershipBeforeMovements[section]?.numberOfShares
                  )}
                  normalize
                  {...numericBasicFieldFormattingProps}
                  autoHeight
                />
              </td>
            </tr>
            <tr>
              <td
                className={classNames(styles[`${PARENT_CLASSNAME}__label-column`], {
                  [styles[`${PARENT_CLASSNAME}__label-column--italic`]]:
                    (isProRataSelected && isProRataAmountAdjusted) ||
                    (isManualSelected && isManualAmountAdjusted) ||
                    (isProRataSelected && isProRataSharesAdjusted) ||
                    (isManualSelected && isManualSharesAdjusted),
                })}>
                {shouldShowRealBuybackValue ? l('_NominalAdjustment') : l('_Adjustment')}{' '}
              </td>
              <CapitalStructureValuesTableCell
                equityItem={equityItem}
                renderingSplitType={renderingSplitType}
                isProRata={isProRata}
                tableSection={TableSection.AmountAdjustment}
                ownershipIndex={ownershipIndex}
                section={section}
              />
              <CapitalStructureValuesTableCell
                equityItem={equityItem}
                renderingSplitType={renderingSplitType}
                isProRata={isProRata}
                tableSection={TableSection.SharesAdjustment}
                ownershipIndex={ownershipIndex}
                section={section}
              />
            </tr>
            <tr>
              <td
                className={classNames(
                  styles[`${PARENT_CLASSNAME}__label-column`],
                  styles[`${PARENT_CLASSNAME}__label-column--bold`],
                  {
                    [styles[`${PARENT_CLASSNAME}__label-column--italic`]]:
                      (isProRataSelected && isProRataAmountClosing) ||
                      (isManualSelected && isManualAmountClosing) ||
                      (isProRataSelected && isProRataSharesClosing) ||
                      (isManualSelected && isManualSharesClosing),
                  }
                )}>
                {l('_ClosingBalance')}
              </td>
              <CapitalStructureValuesTableCell
                equityItem={equityItem}
                renderingSplitType={renderingSplitType}
                isProRata={isProRata}
                tableSection={TableSection.AmountClosingBalance}
                ownershipIndex={ownershipIndex}
                section={section}
              />
              <CapitalStructureValuesTableCell
                equityItem={equityItem}
                renderingSplitType={renderingSplitType}
                isProRata={isProRata}
                tableSection={TableSection.SharesClosingBalance}
                ownershipIndex={ownershipIndex}
                section={section}
              />
            </tr>
            {shouldShowRealBuybackValue && (
              <tr>
                <td
                  className={classNames(
                    styles[`${PARENT_CLASSNAME}__label-column`],
                    styles[`${PARENT_CLASSNAME}__label-column--bold`]
                  )}>
                  {l('_RealBuybackValue')}
                </td>
                <CapitalStructureValuesTableCell
                  equityItem={equityItem}
                  renderingSplitType={renderingSplitType}
                  isProRata={isProRata}
                  tableSection={TableSection.RealBuybackValue}
                  ownershipIndex={ownershipIndex}
                  section={section}
                />
              </tr>
            )}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default CapitalStructureEditTable;
